import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useShop } from "@/context/ShopContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, formatPrice, resolveImageUrl } from "@/lib/api";
import { getStoredUtm } from "@/lib/utm";
import { toast } from "sonner";
import { Check, Truck, MapPin, Banknote, Smartphone, CreditCard, Sparkles, Clock } from "lucide-react";

// Same non-cash markup as the backend (server.py PAYMENT_METHOD_MARKUP) -
// card/SBP verified against cordstore.ru's live checkout (112 900 -> 129 835,
// x1.15). Split uses the same 1.195 markup as the product-page badge/widget
// (YandexPayBadge.js) - the customer already saw that inflated total there,
// so the order should record the same number, not the bare cash price.
// Which of these are actually offered is admin-editable (settings
// payment_methods_enabled) - the "Скоро будет доступно" note only shows for
// a method that's currently off, so it disappears the moment admin enables it.
const PAYMENT_METHODS = [
  { value: "cash", label: "Наличный расчёт", icon: Banknote, markup: 1 },
  { value: "sbp", label: "Оплата по СБП (+15%)", icon: Smartphone, markup: 1.15 },
  { value: "card", label: "Банковской картой (+15%)", icon: CreditCard, markup: 1.15 },
  { value: "split", label: "Яндекс Сплит", icon: Sparkles, markup: 1.195, note: "Ссылку на оплату пришлёт менеджер" },
];

export default function Checkout() {
  const { cart, clearCart } = useShop();
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [placed, setPlaced] = useState(null);
  const [pickupAddress, setPickupAddress] = useState("");
  const [workHours, setWorkHours] = useState({ start: 10, end: 20, isOpenNow: true });
  const [enabledMethods, setEnabledMethods] = useState({ cash: true, sbp: false, card: false, split: true });
  const [form, setForm] = useState({
    customer_name: user?.name || "",
    phone: user?.phone || "",
    email: user?.email || "",
    delivery_method: "delivery",
    address: user?.address || "",
    delivery_notes: "",
    payment_method: "cash",
  });

  useEffect(() => {
    api.get("/settings").then((r) => {
      setPickupAddress(r.data.pickup_address || "");
      setWorkHours({
        start: r.data.working_hours_start ?? 10,
        end: r.data.working_hours_end ?? 20,
        isOpenNow: r.data.is_open_now ?? true,
      });
      const methods = r.data.payment_methods_enabled;
      if (methods) {
        setEnabledMethods(methods);
        // Default selection may have just been disabled by the admin - fall
        // back to cash rather than submitting a method checkout no longer offers.
        setForm((f) => (methods[f.payment_method] ? f : { ...f, payment_method: "cash" }));
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const workHoursLabel = `с ${String(workHours.start).padStart(2, "0")}:00 до ${String(workHours.end).padStart(2, "0")}:00`;

  if (cart.items.length === 0 && !placed) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="text-3xl font-bold text-ink">Корзина пуста</h1>
        <Link to="/" className="mt-6 inline-block rounded-full bg-ink px-6 py-3 text-sm font-semibold text-white">В каталог</Link>
      </div>
    );
  }

  if (placed) {
    return (
      <div data-testid="checkout-success" className="mx-auto max-w-3xl px-4 py-24 text-center">
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-success/10 text-success">
          <Check size={26} />
        </div>
        <h1 className="mt-6 text-4xl font-extrabold tracking-tightest text-ink">Заказ оформлен!</h1>
        <p className="mt-3 text-sm text-muted">Номер: <span className="font-semibold text-ink">{placed.order_no}</span></p>
        {placed.placed_outside_hours ? (
          <p className="mt-2 text-sm text-muted">
            Сейчас нерабочее время (принимаем звонки {placed.working_hours_label || workHoursLabel}) — свяжемся
            с вами по телефону {placed.phone}, как только откроемся.
          </p>
        ) : (
          <p className="mt-2 text-sm text-muted">Мы свяжемся с вами по телефону {placed.phone} в течение 15 минут.</p>
        )}
        <div className="mt-8 flex justify-center gap-3">
          {user ? (
            <Link to="/account" className="rounded-full bg-ink px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-ink-soft">Мои заказы</Link>
          ) : (
            <Link to="/register" className="rounded-full bg-ink px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-ink-soft">Создать аккаунт</Link>
          )}
          <Link to="/" className="rounded-full border border-ink px-6 py-3 text-sm font-semibold text-ink transition-colors hover:bg-ink hover:text-white">На главную</Link>
        </div>
      </div>
    );
  }

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const isPickup = form.delivery_method === "pickup";
  const paymentMarkup = PAYMENT_METHODS.find((m) => m.value === form.payment_method)?.markup || 1;
  const orderTotal = Math.round(cart.subtotal * paymentMarkup);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", {
        ...form,
        email: form.email.trim() || undefined,
        items: cart.items.map((it) => ({
          product_id: it.product_id,
          variation_id: it.variation_id,
          quantity: it.quantity,
        })),
        utm: getStoredUtm() || undefined,
      });
      setPlaced(data);
      if (window.ym) window.ym(111698654, "reachGoal", "order_success", { order_price: data.total });
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        ecommerce: {
          purchase: {
            actionField: { id: data.order_no, revenue: data.total },
            products: cart.items.map((it) => ({
              id: it.product_id,
              name: it.product_name,
              price: it.price,
              quantity: it.quantity,
            })),
          },
        },
      });
      await clearCart();
    } catch (err) {
      toast.error(formatApiError(err, "Не удалось оформить заказ"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="checkout-page" className="mx-auto max-w-7xl px-4 py-10 lg:px-8">
      <div className="mb-8">
        <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-muted-soft">Финал</div>
        <h1 className="mt-2 text-4xl font-extrabold tracking-tightest text-ink md:text-5xl">Оформление заказа</h1>
      </div>

      {!workHours.isOpenNow && (
        <div data-testid="working-hours-notice" className="mb-6 flex items-start gap-3 rounded-2xl border border-warning/30 bg-warning/10 px-5 py-4 text-sm text-ink">
          <Clock size={18} className="mt-0.5 shrink-0 text-warning" />
          <p>
            Сейчас нерабочее время — принимаем звонки и обрабатываем заказы {workHoursLabel}. Заказ можно оформить
            прямо сейчас, менеджер свяжется с вами, как только мы откроемся.
          </p>
        </div>
      )}

      <form onSubmit={submit} className="grid grid-cols-1 gap-8 lg:grid-cols-12">
        <div className="space-y-6 lg:col-span-8">
          <div className="rounded-2xl border border-line bg-white p-6">
            <h2 className="text-lg font-bold text-ink">Контакты</h2>
            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className="block">
                <span className="mb-1 block text-xs font-semibold text-muted">ФИО *</span>
                <input required data-testid="checkout-name" value={form.customer_name} onChange={onChange("customer_name")} className="w-full rounded-lg border border-line bg-white px-4 py-3 text-sm outline-none focus:border-ink" />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-semibold text-muted">Телефон *</span>
                <input required data-testid="checkout-phone" value={form.phone} onChange={onChange("phone")} placeholder="+7 (___) ___-__-__" className="w-full rounded-lg border border-line bg-white px-4 py-3 text-sm outline-none focus:border-ink" />
              </label>
              <label className="block md:col-span-2">
                <span className="mb-1 block text-xs font-semibold text-muted">Email</span>
                <input type="email" data-testid="checkout-email" value={form.email} onChange={onChange("email")} className="w-full rounded-lg border border-line bg-white px-4 py-3 text-sm outline-none focus:border-ink" />
              </label>
            </div>
          </div>

          <div className="rounded-2xl border border-line bg-white p-6">
            <h2 className="text-lg font-bold text-ink">Способ получения</h2>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <button
                type="button"
                data-testid="delivery-method-delivery"
                onClick={() => setForm((f) => ({ ...f, delivery_method: "delivery" }))}
                className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${
                  !isPickup ? "border-ink bg-ink text-white" : "border-line bg-white text-ink hover:border-ink"
                }`}
              >
                <Truck size={18} />
                <div>
                  <div className="text-sm font-semibold">Доставка</div>
                  <div className={`text-xs ${!isPickup ? "text-white/70" : "text-muted"}`}>По адресу, курьером</div>
                </div>
              </button>
              <button
                type="button"
                data-testid="delivery-method-pickup"
                onClick={() => setForm((f) => ({ ...f, delivery_method: "pickup" }))}
                className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${
                  isPickup ? "border-ink bg-ink text-white" : "border-line bg-white text-ink hover:border-ink"
                }`}
              >
                <MapPin size={18} />
                <div>
                  <div className="text-sm font-semibold">Самовывоз</div>
                  <div className={`text-xs ${isPickup ? "text-white/70" : "text-muted"}`}>Бесплатно, в день заказа</div>
                </div>
              </button>
            </div>

            <div className="mt-4">
              {isPickup ? (
                <div data-testid="pickup-address" className="rounded-xl border border-line bg-paper px-4 py-3 text-sm text-ink">
                  <div className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-soft">Адрес самовывоза</div>
                  <div className="mt-1">{pickupAddress || "Уточняется"}</div>
                </div>
              ) : (
                <>
                  <label className="block">
                    <span className="mb-1 block text-xs font-semibold text-muted">Адрес доставки *</span>
                    <input required data-testid="checkout-address" value={form.address} onChange={onChange("address")} placeholder="Город, улица, дом, квартира" className="w-full rounded-lg border border-line bg-white px-4 py-3 text-sm outline-none focus:border-ink" />
                  </label>
                  <p data-testid="delivery-cost-note" className="mt-2 text-xs text-muted">
                    Стоимость и сроки доставки менеджер рассчитает и согласует с вами после оформления заказа.
                  </p>
                </>
              )}
              <label className="mt-4 block">
                <span className="mb-1 block text-xs font-semibold text-muted">Комментарий к заказу</span>
                <textarea data-testid="checkout-notes" value={form.delivery_notes} onChange={onChange("delivery_notes")} rows={3} className="w-full rounded-lg border border-line bg-white px-4 py-3 text-sm outline-none focus:border-ink" />
              </label>
            </div>
          </div>

          <div className="rounded-2xl border border-line bg-white p-6">
            <h2 className="text-lg font-bold text-ink">Способ оплаты</h2>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {PAYMENT_METHODS.map(({ value, label, icon: Icon, note: defaultNote }) => {
                const active = form.payment_method === value;
                const disabled = !enabledMethods[value];
                const note = disabled ? "Скоро будет доступно" : defaultNote;
                return (
                  <button
                    key={value}
                    type="button"
                    disabled={disabled}
                    data-testid={`payment-method-${value}`}
                    onClick={() => !disabled && setForm((f) => ({ ...f, payment_method: value }))}
                    className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${
                      disabled
                        ? "cursor-not-allowed border-line bg-paper text-muted opacity-60"
                        : active
                          ? "border-ink bg-ink text-white"
                          : "border-line bg-white text-ink hover:border-ink"
                    }`}
                  >
                    <Icon size={18} />
                    <div>
                      <div className="text-sm font-semibold">{label}</div>
                      {note && <div className={`text-xs ${active && !disabled ? "text-white/70" : "text-muted"}`}>{note}</div>}
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="mt-3 text-xs text-muted">
              Менеджер свяжется с вами для подтверждения заказа.
            </p>
          </div>
        </div>

        <aside className="lg:col-span-4">
          <div className="sticky top-24 rounded-2xl border border-line bg-white p-6">
            <h2 className="text-lg font-bold text-ink">Ваш заказ</h2>
            <ul className="mt-4 max-h-72 space-y-3 overflow-y-auto pr-2">
              {cart.items.map((it) => (
                <li key={`${it.product_id}-${it.variation_id}`} className="flex items-center gap-3 text-sm">
                  <img src={resolveImageUrl(it.image)} alt="" className="h-12 w-12 rounded-lg object-cover" />
                  <div className="flex-1">
                    <div className="line-clamp-1 font-semibold text-ink">{it.product_name}</div>
                    <div className="text-xs text-muted">{it.variation.color}{(it.variation.memory || it.variation.storage) ? ` · ${it.variation.memory || it.variation.storage}` : ""} × {it.quantity}</div>
                  </div>
                  <div className="font-semibold text-ink">{formatPrice(it.line_total)}</div>
                </li>
              ))}
            </ul>
            <div className="mt-4 border-t border-line pt-4">
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-muted">К оплате</span>
                <span className="text-2xl font-extrabold tracking-tightest text-ink">{formatPrice(orderTotal)}</span>
              </div>
              {paymentMarkup > 1 && (
                <div className="mt-1 text-right text-xs text-muted">{formatPrice(cart.subtotal)} + {(Math.round((paymentMarkup - 1) * 1000) / 10).toString().replace(".0", "")}%</div>
              )}
            </div>
            <button
              data-testid="place-order-btn"
              disabled={submitting}
              type="submit"
              className="mt-6 w-full rounded-full bg-ink py-3 text-sm font-semibold text-white transition-colors hover:bg-ink-soft disabled:opacity-50"
            >
              {submitting ? "Оформляем…" : "Подтвердить заказ"}
            </button>
          </div>
        </aside>
      </form>
    </div>
  );
}
